import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    AbilityLookup,
    Action,
    ActionUtils,
    BaseFeat,
    characterActions,
    CharacterTheme,
    DataOriginRefData,
    Race,
    RaceUtils,
    RacialTrait,
    RacialTraitUtils,
    RuleData,
    rulesEngineSelectors,
    SnippetData,
    Spell,
    SpellUtils,
} from '@dndbeyond/character-rules-engine/es';

import { RacialTraitFeatureSnippet } from '../../../../CharacterSheet/components/FeatureSnippet';
import { sidebarActions } from '../../../actions';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils } from '../../../utils';
import { PaneIdentifiersRacialTrait } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    race: Race | null;
    identifiers: PaneIdentifiersRacialTrait | null;
    feats: Array<BaseFeat>;
    snippetData: SnippetData;
    ruleData: RuleData;
    abilityLookup: AbilityLookup;
    dataOriginRefData: DataOriginRefData;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
interface State {
    racialTrait: RacialTrait | null;
}
class RacialTraitPane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { race, identifiers } = this.props;

        if (race !== prevProps.race || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { race, identifiers } = props;

        let foundRacialTrait: RacialTrait | null | undefined = null;
        if (race !== null && identifiers !== null) {
            foundRacialTrait = RaceUtils.getVisibleRacialTraits(race).find(
                (racialTrait) => identifiers.id === RacialTraitUtils.getId(racialTrait),
            );
        }

        return {
            racialTrait: foundRacialTrait ? foundRacialTrait : null,
        };
    };

    handleActionUseSet = (action: Action, uses: number): void => {
        const { dispatch } = this.props;

        const id = ActionUtils.getId(action);
        const entityTypeId = ActionUtils.getEntityTypeId(action);
        if (id !== null && entityTypeId !== null) {
            dispatch(characterActions.actionUseSet(id, entityTypeId, uses, ActionUtils.getDataOriginType(action)));
        }
    };

    handleSpellUseSet = (spell: Spell, uses: number): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        const mappingEntityTypeId = SpellUtils.getMappingEntityTypeId(spell);
        if (mappingId !== null && mappingEntityTypeId !== null) {
            dispatch(
                characterActions.spellUseSet(mappingId, mappingEntityTypeId, uses, SpellUtils.getDataOriginType(spell)),
            );
        }
    };

    handleSpellDetailShow = (spell: Spell): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        if (mappingId !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.CHARACTER_SPELL_DETAIL,
                    PaneIdentifierUtils.generateCharacterSpell(mappingId),
                ),
            );
        }
    };

    handleRacialTraitShow = (racialTrait: RacialTrait): void => {
        const { dispatch } = this.props;
        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.RACIAL_TRAIT_DETAIL,
                PaneIdentifierUtils.generateRacialTrait(RacialTraitUtils.getId(racialTrait)),
            ),
        );
    };

    handleActionShow = (action: Action): void => {
        const { dispatch } = this.props;

        const mappingId = ActionUtils.getMappingId(action);
        const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);
        if (mappingId !== null && mappingEntityTypeId !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.ACTION,
                    PaneIdentifierUtils.generateAction(mappingId, mappingEntityTypeId),
                ),
            );
        }
    };

    render() {
        const { racialTrait } = this.state;
        const {
            race,
            feats,
            abilityLookup,
            snippetData,
            ruleData,
            dataOriginRefData,
            isReadonly,
            proficiencyBonus,
            theme,
        } = this.props;

        if (race === null || racialTrait === null) {
            return <PaneInitFailureContent />;
        }

        let fullName = RaceUtils.getFullName(race);
        let portrait = RaceUtils.getPortraitAvatarUrl(race);

        return (
            <div className="ct-racial-trait-pane" key={RacialTraitUtils.getId(racialTrait)}>
                <SidebarHeader parent={fullName} previewImage={portrait ? portrait : ''}>
                    {RacialTraitUtils.getName(racialTrait)}
                </SidebarHeader>
                <RacialTraitFeatureSnippet
                    racialTrait={racialTrait}
                    onActionUseSet={this.handleActionUseSet}
                    onActionClick={this.handleActionShow}
                    onSpellUseSet={this.handleSpellUseSet}
                    onSpellClick={this.handleSpellDetailShow}
                    onFeatureClick={this.handleRacialTraitShow}
                    showHeader={false}
                    showDescription={true}
                    feats={feats}
                    snippetData={snippetData}
                    ruleData={ruleData}
                    abilityLookup={abilityLookup}
                    dataOriginRefData={dataOriginRefData}
                    isReadonly={isReadonly}
                    proficiencyBonus={proficiencyBonus}
                    theme={theme}
                />
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        race: rulesEngineSelectors.getRace(state),
        feats: rulesEngineSelectors.getBaseFeats(state),
        snippetData: rulesEngineSelectors.getSnippetData(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        dataOriginRefData: rulesEngineSelectors.getDataOriginRefData(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
    };
}

export default connect(mapStateToProps)(RacialTraitPane);
